import DebugLocationPopup from "@components/Debug/LocationPopup";
import SystemError from "@components/SystemError";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import PulseLoader from "@hudson/ui/components/Loader/PulseLoader";
import { RegisterOptions, useFormContext } from "@hudson/ui/components/ReactHookForm";
import { useAdjustFrameForPopup } from "@hudson/ui/lib/use_autosize";
import normalStyles from "@hudson/ui/styles/Form.module.css";
import useConfig from "@lib/use_config";
import useContent from "@lib/use_content";
import { findLocations as defaultFindLocations } from "@services/find_locations/index";
import isValidLocation from "@services/is_valid_location";
import useDebounce from "@utils/use_debounce";
import chroma from "chroma-js";
import { motion } from "framer-motion";
import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";
import { Search, SearchProps } from "semantic-ui-react";
import ClearButton from "./internal/ClearButton";
import FixedLocationDialog from "./internal/FixedLocationDialog";
import defaultLocationPredictionSelected from "./internal/location_prediction_selected";
import NearbyLocationsDialog from "./internal/NearbyLocationsDialog";
import NotFoundMessage from "./internal/NotFoundMessage";
import { Place } from "./internal/Place";
import useFixedLocationsPlaceList from "./internal/use_fixed_locations";
import styles from "./Location.module.css";
import TypeIcon from "./TypeIcon";

interface LocationProps {
  className?: string;
  disabled?: boolean;
  fieldName: string;
  fieldValidations?: RegisterOptions;
  findLocations?: be.FindLocationsFunction;
  focusOnMount?: boolean;
  keepValueOnUnregister?: boolean;
  hidePuds?: boolean;
  limitLocations?: number;
  locationPredictionRenderer?: JSX.Element;
  locationPredictionSelected?: be.LocationPredictionSelectedFunction;
  noAutosizing?: boolean;
  normalFieldStyle?: boolean;
  onLocationChange?: () => void;
  placeholder?: string;
  required?: boolean;
  searchCenter?: be.LatLng;
  serviceAreaFilter?: number | number[];
}

function truncateWithEllipses(text: string, max: number): string {
  return text.substr(0, max - 1) + (text.length > max ? "..." : "");
}

function Location({
  className,
  disabled,
  fieldName,
  fieldValidations = {},
  findLocations = defaultFindLocations,
  focusOnMount,
  hidePuds,
  keepValueOnUnregister = true,
  limitLocations,
  locationPredictionRenderer = Place,
  locationPredictionSelected = defaultLocationPredictionSelected,
  noAutosizing,
  normalFieldStyle,
  onLocationChange,
  placeholder,
  required = true,
  searchCenter,
  serviceAreaFilter,
}: LocationProps): JSX.Element | null {
  let fixedLocationList = useFixedLocationsPlaceList(serviceAreaFilter, hidePuds);
  const { loading, data, error } = useConfig(["defaultSearchCenter { lat lng }"]);
  const [showSystemError, setShowSystemError] = useState(false);
  const {
    data: cData,
    loading: cLoading,
    error: cError,
  } = useContent([
    "beErrLocTooGeneral",
    "beErrNoFareKey",
    "beErrNoLocsFound",
    "beErrFieldRequired",
  ]);

  // Focus input field on mount
  useEffect(() => {
    if (focusOnMount) {
      searchFieldRef.current?.focus();
      setResultsOpen(!!fixedLocationList.length);
    }
  }, [fixedLocationList]);

  // Hook input into form
  const { formState, getFieldState, clearErrors, watch, register, setError, setValue, unregister } =
    useFormContext();
  useEffect(() => {
    register(fieldName, {
      ...fieldValidations,
      validate: {
        required: (location) => !!location || !required || cData?.content.beErrFieldRequired,
        locationError: (location) => !location?.error || location?.error,
        badFareKeyLookup: (location) => !!location?.code || cData?.content.beErrNoFareKey,
        validLocation: async (location) =>
          (await isValidLocation(location)) || cData?.content.beErrLocTooGeneral,
      },
    });
    return () => {
      unregister(fieldName, { keepValue: keepValueOnUnregister });
    };
  }, [cData, register, unregister]);
  const value = watch(fieldName);
  const onChange = async (location?: be.Location) => {
    setResults([]);
    (document.activeElement as HTMLElement)?.blur();
    await setValue(fieldName, location, { shouldDirty: true, shouldValidate: true });
    if (onLocationChange) onLocationChange();
  };
  const fieldError = getFieldState(fieldName)?.error;
  const [locLoading, setLocLoading] = useState<boolean>(false);
  const [results, setResults] = useState<be.LocationPrediction[] | ["noResults"]>([]);
  const [resultsLoading, setResultsLoading] = useState<boolean>(false);
  const [resultsOpen, setResultsOpen] = useState<boolean | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(value?.displayName);
  const [showFixed, setShowFixed] = useState<[string, string, number | undefined] | undefined>();

  useAdjustFrameForPopup({
    enabled: !noAutosizing,
    isShown: resultsOpen,
    popupQuery: ".results.visible",
    extraHeight: 10,
    useInterval: true,
    label: "locations",
  });

  // Needed to keep field from gainig focus after nearby locs
  const [loseFocus, setLoseFocus] = useState<boolean>(false);
  const [showNearby, setShowNearby] = useState<
    [be.Location, Array<any>, be.Location | undefined] | undefined
  >();
  const searchTermRef = useRef<string | undefined>();
  const searchFieldRef = useRef<HTMLInputElement | null>(null);
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Ensure we have access to the most current search term from within useEffect.
  searchTermRef.current = searchTerm;

  // If value changes, update field value
  useEffect(() => setSearchTerm(value?.displayName || ""), [value?.displayName]); // eslint-disable-line react-hooks/exhaustive-deps

  // Respond to search term
  useEffect(() => {
    if (debouncedSearchTerm && debouncedSearchTerm != value?.displayName) {
      setResults([]);
      loadLocations({ limitLocations }).then(function (predictions) {
        setResults(predictions.length == 0 ? ["noResults"] : predictions);
        if (debouncedSearchTerm == searchTermRef.current) setResultsLoading(false);
      });
    }
  }, [debouncedSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  function loadLocations(options: be.FindLocationsFunctionOptions = {}) {
    return findLocations({
      searchTerm: debouncedSearchTerm,
      searchCenter: searchCenter || data?.config.defaultSearchCenter,
      searchRadius: 30000,
      serviceArea: serviceAreaFilter,
      options,
    });
  }

  function onResultSelect(
    _: MouseEvent,
    { defaultBehavior, result }: { defaultBehavior: boolean; result: be.LocationPrediction },
  ): void {
    const fn = defaultBehavior ? defaultLocationPredictionSelected : locationPredictionSelected;
    setResultsOpen(undefined);
    fn({
      prediction: result,
      serviceAreaFilter: serviceAreaFilter,
      fns: {
        clearErrors: () => clearErrors(fieldName),
        clearResults: () => setResults([]),
        runDefaultBehavior: (prediction) =>
          onResultSelect(undefined, { result: prediction, defaultBehavior: true }),
        keepDialogOpen: () => setResultsOpen(true),
        loadLocations: (options) => loadLocations({ limitLocations, ...options }),
        updateLocation: (location) => onChange(location),
        updateLocationLoadingStatus: (on) => setLocLoading(on),
        showFixedLocationPopup: (type, typeString, serviceArea) => {
          document.activeElement?.blur();
          setShowFixed([type, typeString, serviceArea]);
        },
        showLoadingAnimation: (on) => setResultsLoading(on),
        showNearbyLocationsPopup: (
          anchorPoint: be.Location,
          locations: be.Location[],
          requestedLocation?: be.Location,
        ) => {
          document.activeElement?.blur();
          setLoseFocus(true);
          setShowNearby([anchorPoint, locations, requestedLocation]);
        },
        showResults: (predictions) => setResults(predictions),
        showSystemError: () => setShowSystemError(true),
        updateSearchTerm: (term: string) => setSearchTerm(term),
      },
    });
  }

  function onSearchChange(_: MouseEvent, data: SearchProps): void {
    setResultsOpen(true);
    if (!data.value) setResults([]);
    setResultsLoading(true);
    setSearchTerm(data.value);
  }

  let addressDisplay;
  if (value?.displayAddress)
    addressDisplay = (
      <div
        className={`pl-[4rem] pr-[3.5rem] width-[100%]  text-gray-600  font-thin text-sm truncate ${
          locLoading ? "invisible" : "visible"
        }`}
      >
        {truncateWithEllipses(value.displayAddress, 60)}
      </div>
    );

  return error || cError ? null : (
    <>
      {showSystemError && <SystemError onHide={() => setShowSystemError(false)} />}
      <div className="relative flex-1 min-w-0">
        {showFixed && (
          <FixedLocationDialog
            show={!!showFixed}
            locationType={showFixed[0]}
            locationTypeString={showFixed[1]}
            onHide={() => setShowFixed(undefined)}
            onLocationSelect={function (loc, locationType) {
              onResultSelect(undefined, {
                result: {
                  source: "hudson",
                  title: loc.displayName,
                  description: loc.displayAddress,
                  partiallocation: locationType == "PUD" ? loc : undefined,
                  key: loc.code,
                  type: loc.typeString,
                },
              });
            }}
            serviceArea={showFixed[2]}
            noAutosizing={noAutosizing}
          />
        )}
        {showNearby && (
          <NearbyLocationsDialog
            show={!!showNearby}
            anchorPoint={showNearby[0]}
            locations={showNearby[1]}
            requestedLocation={showNearby[2]}
            onHide={() => {
              onChange();
              setShowNearby(undefined);
            }}
            onLocationSelect={function (loc) {
              onResultSelect(undefined, {
                result: {
                  source: "hudson",
                  title: loc.displayName,
                  description: loc.displayAddress,
                  location: loc,
                  key: loc.code,
                  servicearea: loc.serviceAreas,
                  type: loc.typeString,
                },
              });
            }}
          />
        )}
        {/* shadow-sm block text-base border-gray-400 pt-[0.75rem] pb-[0.5rem] */}
        <motion.div
          className={`${normalFieldStyle ? `${normalStyles.input} ${styles.normalContainer}` : `${styles.container} flex-1 self-start max-w-full`} relative ${className}`}
        >
          <DebugLocationPopup
            label="Location details"
            location={value}
            className="absolute mb-4 text-sm font-bold -top-5"
          />

          <TypeIcon
            className={`absolute h-8 ${value?.displayAddress ? "top-5" : "top-2"} left-5 z-10`}
            type={value?.typeString}
            typeKey={value?.type}
          />

          {/* "h-[1.75rem] pl-[4rem] pr-[3.5rem] border-0 py-0" */}
          <Search
            className={`${normalFieldStyle ? styles.normalSearch : styles.search} text-xl `}
            disabled={disabled || loading || cLoading}
            input={{ ref: searchFieldRef }}
            onBlur={function () {
              setSearchTerm(value?.displayName?.toString() || "");
              setResultsOpen(undefined);
            }}
            onFocus={() => {
              if (loseFocus) {
                document.activeElement?.blur();
                setResultsOpen(false);
                setLoseFocus(false);
              } else {
                setResults([]);
                setResultsOpen(!!fixedLocationList.length);
              }
            }}
            onResultSelect={onResultSelect}
            onSearchChange={onSearchChange}
            open={resultsOpen}
            minCharacters={2}
            noResultsMessage={
              resultsLoading ? (
                <ConcentricLoader />
              ) : (
                <NotFoundMessage message={cData?.content.beErrNoLocsFound} />
              )
            }
            placeholder={loading || cLoading ? "..." : placeholder}
            resultRenderer={locationPredictionRenderer}
            results={
              results.length == 1 && results[0] == "noResults"
                ? []
                : fixedLocationList.length
                  ? [...results, ...(results.length ? [] : fixedLocationList)]
                  : results
            }
            value={searchTerm}
          />

          {addressDisplay}

          {fieldError && (
            <div
              style={{ backgroundColor: chroma("red").set("hsl.s", 0.63).set("hsl.l", 0.56).hex() }}
              className={`rounded-b-md relative -bottom-2 w-full inline-flex items-center px-2.5 py-1 pt-1.5 text-sm font-medium text-white`}
            >
              {fieldError.message}
            </div>
          )}

          {locLoading ? (
            <PulseLoader
              className={`absolute right-5 ${value?.displayAddress ? "top-4" : "top-2"}`}
              colorVar="primary-color"
            />
          ) : disabled ? null : (
            <ClearButton
              className={`absolute right-1 ${value?.displayAddress ? "top-2" : "top-0"}`}
              visible={!!searchTerm}
              onClick={function () {
                setSearchTerm("");
                setResults([]);
                onChange();
              }}
            />
          )}
        </motion.div>
      </div>
    </>
  );
}

export default dynamic(() => Promise.resolve(Location), {
  ssr: false,
});
